import { useMemo } from "react";
import { resolveAppearanceTokens, useTheme } from "@salestrak/ui";

import { useThemeAppearance } from "#app/components/theme/theme-appearance-provider";
import {
  buildMapTileTheme,
  type MapShade,
  type MapTileTheme,
} from "./map-styles";

/**
 * The Google basemap styled to the live appearance.
 *
 * Google's styler consumes literal hex — the one map surface `var(--token)`
 * cannot reach — so the tile palette derives from the SAME seed resolution
 * the document paints from (`resolveAppearanceTokens`), never from probing
 * the painted DOM. Reading `appearance` (not `committed`) means a hovered
 * theme preview restyles the tiles with the rest of the page, and mode flips
 * arrive through `resolvedTheme` in the same render.
 *
 * `shade` picks a lighter or darker GROUND within the current mode's own
 * family — never the other mode's palette, which dragged the control fills
 * across the theme boundary and clashed with the themed popovers.
 */
export function useMapTileTheme(shade: MapShade = "lighter"): MapTileTheme {
  const { appearance } = useThemeAppearance();
  const { resolvedTheme } = useTheme();
  return useMemo(
    () =>
      buildMapTileTheme(
        resolveAppearanceTokens(appearance, resolvedTheme),
        resolvedTheme === "dark",
        shade,
      ),
    [appearance, resolvedTheme, shade],
  );
}
